import { forwardRef } from 'react';
import { useTranslation } from '@/contexts/i18n-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import styles from './drop-zone.module.css';
import clsx from 'clsx';
import { ErrorField } from '@/components/error-field';

interface DropZoneProps {
  onChange: (files: File[]) => void;
  title: string;
  description: string;
  children: React.ReactNode;
  showUploadMoreButton?: boolean;
  hasFiles: boolean;
  inputField: React.ReactNode;
  error?: string;
}

export const DropZone = forwardRef<HTMLElement, DropZoneProps>(
  (
    { onChange, title, description, children, hasFiles, inputField, showUploadMoreButton, error },
    ref,
  ) => {
    const { t } = useTranslation();

    const isMobile = useMediaQuery('--screen-md');

    // Stops dropped files from being opened in the browser
    const stopEventPropagation = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      stopEventPropagation(e);
      onChange(Array.from(e.dataTransfer.files));
    };

    return !hasFiles ? (
      <div
        role="presentation"
        className={styles.dropZone}
        onDragOver={stopEventPropagation}
        onDrop={handleDrop}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        {!isMobile && <h3 className={clsx('body-3', styles.title)}>{title}</h3>}
        <p className={clsx('body-3', styles.description)}>{description}</p>

        {!isMobile && (
          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <span>{t('common.or')}</span>
            <span className={styles.dividerLine} />
          </div>
        )}
        {inputField}
        {error && <ErrorField error={{ type: 'validate', message: error }} />}
      </div>
    ) : (
      <div className={styles.dropZoneWithContent} ref={ref as React.RefObject<HTMLDivElement>}>
        <div className={styles.list}>
          {children}
          {showUploadMoreButton && inputField}
        </div>
        {error && <ErrorField error={{ type: 'validate', message: error }} />}
      </div>
    );
  },
);

DropZone.displayName = 'DropZone';
